const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024;

/**
 * Shrink a large image in the browser before uploading it. Phone photos are often 5-12 MB and
 * 4000px+, which makes uploads slow and server-side processing (e.g. background removal) slower.
 *
 * Never throws: if the image can't be decoded or resizing doesn't help, the original file is returned.
 */
export async function downscaleImage(file: File, maxSide: number): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= SKIP_BELOW_BYTES) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    // Keep PNG for PNG (logos often rely on transparency); everything else becomes JPEG.
    const keepPng = file.type === "image/png";
    const type = keepPng ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, 0.9),
    );
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + (keepPng ? ".png" : ".jpg");
    return new File([blob], name, { type });
  } catch {
    return file;
  }
}
